import { useCallback } from 'react';
import { StageDragResult, StageGroup, StageInfo } from '../types/stageTypes';
import { processDragEnd } from '../utils/stageDragDrop';
import { groupStagesByType } from '../utils/stageGrouping';

/**
 * Custom hook for managing stage drag and drop operations
 * @param stageGroups Current stage groups
 * @param originalStages Original stage data
 * @param setOriginalStages Function to update original stages
 * @param setGroups Function to update stage groups
 * @param clearSelection Function to clear selection
 * @returns Drag and drop handler function
 */
export const useStageDragDrop = (
    stageGroups: StageGroup[],
    originalStages: StageInfo[],
    setOriginalStages: (stages: StageInfo[]) => void,
    setGroups: (groups: StageGroup[]) => void,
    clearSelection: () => void
) => {
    // Handle drag end event
    const handleDragEnd = useCallback((result: StageDragResult) => {
        // Process the drag end event
        const updatedStages = processDragEnd(result, stageGroups, originalStages);

        // If no update was made, return
        if (!updatedStages) return;

        // Update original stages
        setOriginalStages(updatedStages);

        // Update groups based on updated stages
        setGroups(groupStagesByType(updatedStages));

        // Clear selection after drag
        clearSelection();
    }, [stageGroups, originalStages, setOriginalStages, setGroups, clearSelection]);

    return { handleDragEnd };
}; 